import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Avatar from 'boring-avatars';
import {
    AtSignIcon,
    CheckIcon,
    CircleCheckIcon,
    CircleXIcon,
    IdCardLanyardIcon,
    PencilLineIcon,
    PhoneIcon,
    RotateCcwKeyIcon,
    ShieldMinusIcon,
} from 'lucide-react';
import classnames from 'classnames';

import GoBack from '../../components/go-back/go-back';
import View from '../../components/view/view';
import { useAuthStore, useOperatorStore } from '../../store';
import { userSdk } from '../../services/user.service';
import { Operator, RolePermission } from '../../types/operator.type';
import { hasPermissions } from '../../utils/permissions';
import ResponseCodes from '../../constants/response-codes.constant';
import FlatButton from '../../components/flat-button/flat-button';
import { useSettingsStore } from '../../store/settings-store';

import styles from './operator-details.module.scss';
import DeactivateOperatorModal from './containers/deactivate-operator-modal/deactivate-operator-modal';
import UpdateRoleModal from './containers/update-role-modal/update-role-modal';
import { useOperatorDetails } from './hooks/operator-details';

type OperatorDetailsRequest = {
    id: number;
    token: string;
    permissions: RolePermission[];
};

const OperatorDetailsView = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const { theme } = useSettingsStore();
    const { permissions, accessToken } = useAuthStore();
    const { deleteOperator, getRoles, roles } = useOperatorStore();

    const [operator, setOperator] = useState<Operator>();
    const [allPermissions, setAllPermissions] = useState<RolePermission[]>([]);
    const [operatorPermissions, setOperatorPermissions] =
        useState<Map<number, RolePermission>>();
    const [loadingResetPassword, setLoadingResetPassword] = useState(false);
    const [loadingDeactivation, setLoadingDeactivation] = useState(false);
    const [toastData, setToastData] = useState({
        message: <></>,
        shown: false,
    });
    const [modalShown, setModalShown] = useState(false);
    const [updateRoleModalShown, setUpdateRoleModalShown] = useState(false);

    const { updateRole } = useOperatorDetails({
        accessToken,
        operatorId: Number(id),
    });

    useEffect(() => {
        if (isNaN(Number(id))) {
            return navigate('/');
        }
    }, [id, navigate]);

    useEffect(() => {
        accessToken && fetchAllPermissions(accessToken);
    }, [accessToken]);

    useEffect(() => {
        if (!accessToken || permissions.length === 0) return;

        fetchOperatorDetails({
            id: Number(id),
            token: accessToken,
            permissions,
        });

        if (hasPermissions('read-roles', permissions)) {
            getRoles(accessToken);
        }
    }, [accessToken, permissions]);

    const fetchOperatorDetails: (data: OperatorDetailsRequest) => void =
        useCallback(async ({ id, token, permissions }) => {
            if (!hasPermissions('read-operators', permissions)) return;

            const response = await userSdk.getOperatorById(id, token);
            if (response.code !== ResponseCodes.PROCESS_OK) {
                return console.error('Error getting operator details');
            }
            setOperator(response.data);
            const operatorPermissions = response.data!.role.permissions;
            const map = new Map<number, RolePermission>();
            operatorPermissions!.forEach((permission) => {
                map.set(permission.id, permission);
            });

            setOperatorPermissions(map);
        }, []);

    const fetchAllPermissions: (token: string) => Promise<void> = useCallback(
        async (token: string) => {
            const response = await userSdk.getPermissions(token);
            setAllPermissions(response.data!);
        },
        []
    );

    const hidePasswordToast: () => void = useCallback(() => {
        setTimeout(() => {
            setToastData((prev) => ({ ...prev, shown: false }));
        }, 4000);
    }, [setToastData]);

    const handleResetPassword: () => Promise<void> = useCallback(async () => {
        if (!id || !accessToken) return;
        setLoadingResetPassword(true);
        const response = await userSdk.requestPasswordRecovery(
            Number(id),
            accessToken
        );
        setLoadingResetPassword(false);
        if (response.code !== ResponseCodes.PROCESS_OK) {
            setToastData({
                shown: true,
                message: (
                    <>
                        <CircleXIcon size={18} color="white" />
                        <span>Error enviando correo al operador</span>
                    </>
                ),
            });
        } else {
            setToastData({
                shown: true,
                message: (
                    <>
                        <CircleCheckIcon size={18} color="white" />
                        <span>
                            Correo enviado al operador para actualizar
                            contraseña
                        </span>
                    </>
                ),
            });
        }
        hidePasswordToast();
    }, [id, accessToken]);

    const requestDeactivation = useCallback(
        () => setModalShown(true),
        [setModalShown]
    );

    const handleDeactivation: () => Promise<void> = useCallback(async () => {
        if (!accessToken) return;
        setLoadingDeactivation(true);
        await deleteOperator(Number(id), accessToken);
        return navigate(-1);
    }, [accessToken]);

    const openUpdateRoleModal = useCallback(() => {
        setUpdateRoleModalShown(true);
    }, []);

    const handleUpdateRole = async (roleId: number) => {
        setUpdateRoleModalShown(false);
        const updated = await updateRole(roleId);
        if (!updated) return;
        setTimeout(() => navigate(0), 1500);
    };

    return (
        <View
            theme={theme}
            requiredPermission="read-operators"
            permissions={permissions}
        >
            <section className={styles.content}>
                <GoBack theme={theme.current} />
                <header>
                    <Avatar size={80} name={operator?.firstName ?? '-'} />
                    <div className={styles.basicInfo}>
                        <h4>
                            {operator?.firstName + ' ' + operator?.lastName}
                        </h4>
                        <div className={styles.roleSection}>
                            <h6>Rol: {operator?.role.displayName}</h6>
                            {hasPermissions(
                                'update-operator-role',
                                permissions
                            ) && (
                                <div
                                    className={styles.editButton}
                                    onClick={openUpdateRoleModal}
                                >
                                    <PencilLineIcon size={14} />
                                    <span>Editar</span>
                                </div>
                            )}
                        </div>
                    </div>
                </header>
                <section className={styles.details}>
                    <h3>Información</h3>
                    <div className={styles.row}>
                        <AtSignIcon size={18} />
                        <div className={styles.column}>
                            <label>Correo</label>
                            <span>{operator?.email}</span>
                        </div>
                    </div>
                    <div className={styles.row}>
                        <IdCardLanyardIcon size={18} />
                        <div className={styles.column}>
                            <label>Correo</label>
                            <span>{operator?.email}</span>
                        </div>
                    </div>
                    <div className={styles.row}>
                        <PhoneIcon size={18} />
                        <div className={styles.column}>
                            <label>Teléfono</label>
                            <span>
                                {operator?.user.phone ?? '(No asignado)'}
                            </span>
                        </div>
                    </div>
                </section>
                <section>
                    <h3>Acciones</h3>
                    <div style={{ display: 'flex', gap: 16 }}>
                        <FlatButton
                            onClick={handleResetPassword}
                            loading={loadingResetPassword}
                        >
                            <RotateCcwKeyIcon size={18} />
                            <span>Restablecer contraseña</span>
                        </FlatButton>
                        <FlatButton
                            onClick={requestDeactivation}
                            loading={loadingDeactivation}
                        >
                            <ShieldMinusIcon size={18} />
                            <span>Deshabilitar operador</span>
                        </FlatButton>
                    </div>
                    <div className={styles.divider} />
                </section>
                <section className={styles.permissions}>
                    <h3>Permisología</h3>
                    <ul>
                        {allPermissions.map((permission) => (
                            <li key={permission.id}>
                                <div
                                    className={classnames({
                                        [styles.checkbox]: true,
                                        [styles.selected]:
                                            operatorPermissions?.has(
                                                permission.id
                                            ),
                                    })}
                                >
                                    <CheckIcon
                                        size={14}
                                        color="white"
                                        strokeWidth={3}
                                    />
                                </div>
                                <span>{permission.displayName}</span>
                            </li>
                        ))}
                    </ul>
                </section>
            </section>
            <DeactivateOperatorModal
                shown={modalShown}
                onConfirm={handleDeactivation}
                onDiscard={() => setModalShown(false)}
            />
            <UpdateRoleModal
                roles={roles}
                operatorRoleId={operator?.role.id}
                shown={updateRoleModalShown}
                onConfirm={handleUpdateRole}
                onDiscard={() => setUpdateRoleModalShown(false)}
            />
            <div
                className={classnames({
                    [styles.toast]: true,
                    [styles.shown]: toastData.shown,
                })}
            >
                {toastData.message}
            </div>
        </View>
    );
};

export default OperatorDetailsView;
